import { useCallback } from 'react';
import { matchPath } from 'react-router-dom';
import { Route } from '~/Constants/Routes.constants';
import { useMockMode, useProvider, useUser } from '..';
import { accessList } from './accessList';
import { AccessList, AccessListItem, AccessRoles, FitogramVersion } from './accessList.types';
import { PUBLIC_ROUTES } from './accessListRoutes';
import {
  hasFeaterKeyAccess as HFKA,
  hasUserAccess as HUA,
  isOnCorrectAppVersion as IOCAV,
} from './hasAccess.helper';

export const useHasAccess = () => {
  const { isUsingThePartnerTool } = useProvider();
  const {
    DANGEROUSLY_isSuperAdmin,
    DANGEROUSLY_isPartnergramSupportAgent,
    role: userRole,
  } = useUser();
  const { mockedUserView, mockedAppView, isInMockMode } = useMockMode();
  const hasFeaterKeyAccess = useCallback(
    (featureKeyAccess: boolean) =>
      HFKA({
        featureKeyAccess,
        isInMockMode,
        mockedAppView,
        isUsingThePartnerTool,
        DANGEROUSLY_isPartnergramSupportAgent,
        DANGEROUSLY_isSuperAdmin,
        mockedUserView,
      }),
    [
      isInMockMode,
      mockedAppView,
      isUsingThePartnerTool,
      DANGEROUSLY_isPartnergramSupportAgent,
      DANGEROUSLY_isSuperAdmin,
      mockedUserView,
    ],
  );
  const hasUserAccess = useCallback(
    (accessListItem: AccessListItem) =>
      HUA({
        accessListItem,
        userRole,
        isInMockMode,
        DANGEROUSLY_isPartnergramSupportAgent,
        DANGEROUSLY_isSuperAdmin,
        isUsingThePartnerTool,
        mockedAppView,
        mockedUserView,
      }),
    [
      userRole,
      isInMockMode,
      DANGEROUSLY_isPartnergramSupportAgent,
      DANGEROUSLY_isSuperAdmin,
      isUsingThePartnerTool,
      mockedAppView,
      mockedUserView,
    ],
  );
  const isOnCorrectAppVersion = useCallback(
    (fitogramVersions: FitogramVersion[]) =>
      IOCAV({ fitogramVersions, isInMockMode, mockedAppView, isUsingThePartnerTool }),
    [isInMockMode, mockedAppView, isUsingThePartnerTool],
  );

  const hasAccess = useCallback(
    (accessListItemToCheck: AccessList | string) => {
      if (
        typeof accessListItemToCheck === 'string' &&
        PUBLIC_ROUTES.includes(accessListItemToCheck as Route)
      ) {
        return true;
      }
      const matchingAccessItems = accessList.filter((accessListItem) => {
        if (typeof accessListItemToCheck === 'number') {
          return (accessListItem.names as AccessList[]).includes(accessListItemToCheck);
        } else {
          return (accessListItem.names as Route[]).some((route) =>
            matchPath(accessListItemToCheck, {
              path: route,
              exact: true,
              strict: false,
            }),
          );
        }
      });
      if (!matchingAccessItems.length) {
        return false;
      }

      return matchingAccessItems.some((access) => {
        const onCorrectAppVersion = isOnCorrectAppVersion(access.fitogramVersion);
        const userAccess = hasUserAccess(access);
        return onCorrectAppVersion && userAccess;
      });
    },
    [accessList, hasUserAccess, isOnCorrectAppVersion, hasFeaterKeyAccess],
  );

  return {
    hasAccess,
    hasSuperAdminAccess: isInMockMode
      ? mockedUserView === AccessRoles.FITOGRAM_SUPER_ADMIN
      : DANGEROUSLY_isSuperAdmin,
    hasPartnergramSupportAgentAccess: isInMockMode
      ? mockedUserView === AccessRoles.PARTNER_TOOL_ADMIN
      : DANGEROUSLY_isPartnergramSupportAgent,
  };
};
